import { useEffect, useState, useMemo } from "react";
import { AlertTriangle, Clock, TrendingUp, TrendingDown, Send, MessageCircle, Lock, Loader2, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatErr } from "@/lib/api";

const RATING_CLS = {
  excellent: { bg: "bg-emerald-500/10", text: "text-emerald-500", border: "border-emerald-500/40", label: "Excelente" },
  good: { bg: "bg-blue-500/10", text: "text-blue-500", border: "border-blue-500/40", label: "Bueno" },
  fair: { bg: "bg-amber-500/10", text: "text-amber-500", border: "border-amber-500/40", label: "Regular" },
  risk: { bg: "bg-red-500/10", text: "text-red-500", border: "border-red-500/40", label: "Riesgo" },
};

export default function AccountsReceivablePage() {
  const [tab, setTab] = useState("alerts");
  const [currency, setCurrency] = useState("BOB");
  const [alerts, setAlerts] = useState(null);
  const [clients, setClients] = useState([]);
  const [activeClient, setActiveClient] = useState(null);
  const [reminderInv, setReminderInv] = useState(null);

  useEffect(() => {
    api.get(`/admin/com/ar/alerts?currency=${currency}`).then(setAlerts).catch(() => setAlerts(null));
    api.get(`/admin/com/ar/clients?currency=${currency}`).then((d) => setClients(d.clients || [])).catch(() => setClients([]));
  }, [currency]);

  const totals = useMemo(() => {
    if (!alerts) return null;
    const t = (k) => Number(alerts[k]?.total || 0);
    return {
      overdue: t("overdue"), d3: t("due_3d"), d7: t("due_7d"), d14: t("due_14d"), d30: t("soon_30d"),
      sum_critical: t("overdue") + t("due_3d"),
    };
  }, [alerts]);

  return (
    <div data-testid="ar-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1 flex items-center gap-3"><AlertTriangle className="w-8 h-8 text-primary" /> Cuentas por cobrar</h1>
          <p className="text-sm text-muted-foreground">Alertas, salud financiera por cliente y cuentas corrientes · USO INTERNO</p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Moneda</label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="h-10 px-3 bg-background border border-border rounded-sm text-sm" data-testid="ar-currency">
              <option value="BOB">BOB</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border mb-6">
        {[["alerts", `Alertas (${alerts ? (alerts.overdue.count + alerts.due_3d.count + alerts.due_7d.count + alerts.due_14d.count) : 0})`], ["clients", `Clientes (${clients.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ar-tab-${k}`} className={`px-5 py-3 text-sm font-semibold border-b-2 transition-all ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "alerts" && alerts && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Pill label="Vencidas" value={alerts.overdue.count} amount={alerts.overdue.total} cur={currency} cls="border-red-500/40 text-red-500" testid="kpi-overdue" />
            <Pill label="Vencen ≤ 3d" value={alerts.due_3d.count} amount={alerts.due_3d.total} cur={currency} cls="border-amber-500/40 text-amber-500" testid="kpi-3d" />
            <Pill label="Vencen ≤ 7d" value={alerts.due_7d.count} amount={alerts.due_7d.total} cur={currency} cls="border-amber-500/30" testid="kpi-7d" />
            <Pill label="Vencen ≤ 14d" value={alerts.due_14d.count} amount={alerts.due_14d.total} cur={currency} cls="border-blue-500/30 text-blue-500" testid="kpi-14d" />
            <Pill label="Vencen ≤ 30d" value={alerts.soon_30d.count} amount={alerts.soon_30d.total} cur={currency} cls="border-border" testid="kpi-30d" />
          </div>
          <Bucket title="Vencidas" rows={alerts.overdue.items} cur={currency} cls="text-red-500" onRemind={setReminderInv} />
          <Bucket title="Vencen en ≤ 3 días" rows={alerts.due_3d.items} cur={currency} cls="text-amber-500" onRemind={setReminderInv} />
          <Bucket title="Vencen en ≤ 7 días" rows={alerts.due_7d.items} cur={currency} onRemind={setReminderInv} />
          <Bucket title="Vencen en ≤ 14 días" rows={alerts.due_14d.items} cur={currency} onRemind={setReminderInv} />
        </div>
      )}

      {tab === "clients" && (
        <div className="grid lg:grid-cols-2 gap-4">
          {clients.map((c) => {
            const r = RATING_CLS[c.rating];
            return (
              <motion.div key={c.client_id || c.client_name} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                onClick={() => setActiveClient(c)}
                className={`border rounded-sm p-5 cursor-pointer hover:border-primary transition-colors ${r.border}`}
                data-testid={`client-card-${c.client_id || "anon"}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{c.invoices_count} facturas · {c.paid_count} pagadas</p>
                    <h3 className="font-display font-bold text-xl">{c.client_name}</h3>
                  </div>
                  <div className={`text-center ${r.bg} ${r.border} border rounded-sm px-3 py-2`}>
                    <p className={`font-display font-black text-3xl ${r.text}`} data-testid={`score-${c.client_id || "anon"}`}>{c.score}</p>
                    <p className={`text-[9px] uppercase tracking-widest font-bold ${r.text}`}>{r.label}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <Stat label="Facturado" value={`${currency} ${c.total_billed.toLocaleString()}`} />
                  <Stat label="Cobrado" value={`${currency} ${c.total_paid.toLocaleString()}`} highlight="text-emerald-500" />
                  <Stat label="Pendiente" value={`${currency} ${c.total_pending.toLocaleString()}`} highlight={c.total_pending > 0 ? "text-amber-500" : ""} />
                  <Stat label="Vencido ahora" value={`${currency} ${c.total_overdue.toLocaleString()}`} highlight={c.total_overdue > 0 ? "text-red-500" : ""} />
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground border-t border-border pt-2">
                  <span>{c.paid_pct}% pagado</span>
                  <span className="flex items-center gap-1">
                    {c.avg_delay_days > 0 ? <TrendingDown className="w-3 h-3 text-amber-500" /> : <TrendingUp className="w-3 h-3 text-emerald-500" />}
                    {c.avg_delay_days > 0 ? `+${c.avg_delay_days}d demora promedio` : "Pagos al día"}
                  </span>
                  <ArrowRight className="w-3 h-3 ml-auto" />
                </div>
              </motion.div>
            );
          })}
          {clients.length === 0 && <p className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center rounded-sm">Sin facturas en {currency} todavía.</p>}
        </div>
      )}

      {activeClient && <AccountStatement client={activeClient} currency={currency} onClose={() => setActiveClient(null)} onRemind={setReminderInv} />}
      {reminderInv && <ReminderModal invoice={reminderInv} onClose={() => setReminderInv(null)} />}
    </div>
  );
}

function Pill({ label, value, amount, cur, cls = "", testid }) {
  return (
    <div className={`border rounded-sm p-4 ${cls}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">{label}</p>
      <p className="font-display font-black text-3xl tracking-tighter">{value}</p>
      <p className="text-xs mt-1">{cur} {Number(amount || 0).toLocaleString()}</p>
    </div>
  );
}

function Stat({ label, value, highlight = "" }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{label}</p>
      <p className={`font-display font-bold text-sm ${highlight}`}>{value}</p>
    </div>
  );
}

function Bucket({ title, rows, cur, cls = "", onRemind }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mb-6 border border-border rounded-sm overflow-hidden" data-testid={`bucket-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <header className={`px-4 py-2.5 bg-secondary/40 flex items-center justify-between ${cls}`}>
        <h3 className="font-display font-bold text-sm uppercase tracking-widest">{title} <span className="text-xs ml-2 opacity-70">({rows.length})</span></h3>
      </header>
      <table className="w-full text-sm">
        <thead className="bg-secondary/20 text-xs uppercase tracking-widest">
          <tr><th className="text-left p-3">Código</th><th className="text-left p-3">Cliente</th><th className="text-left p-3">Vence</th><th className="text-left p-3">Días</th><th className="text-right p-3">Saldo</th><th className="text-right p-3">Acción</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border hover:bg-secondary/20">
              <td className="p-3 font-mono text-xs">{r.code}</td>
              <td className="p-3 font-semibold">{r.client_name || "—"}</td>
              <td className="p-3 text-xs">{r.due_date}</td>
              <td className={`p-3 text-xs font-bold ${r.days_to_due < 0 ? "text-red-500" : r.days_to_due <= 3 ? "text-amber-500" : ""}`}>
                {r.days_to_due < 0 ? `−${r.days_overdue}d` : `+${r.days_to_due}d`}
              </td>
              <td className="p-3 text-right">{cur} {(Number(r.balance) || 0).toLocaleString()}</td>
              <td className="p-3 text-right">
                <button onClick={() => onRemind(r)} data-testid={`remind-${r.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary"><Send className="w-3 h-3" /> Recordatorio</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountStatement({ client, currency, onClose, onRemind }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!client.client_id) { setData({ movements: [], current_balance: 0, health: client, invoices_count: 0 }); return; }
    api.get(`/admin/com/ar/clients/${encodeURIComponent(client.client_id)}/account?currency=${currency}`).then(setData).catch(() => setData(null));
  }, [client, currency]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-end p-3" onClick={onClose}>
      <div className="bg-background w-full max-w-3xl h-full overflow-y-auto rounded-sm border border-border" onClick={(e) => e.stopPropagation()} data-testid="account-statement">
        <div className="p-5 border-b border-border sticky top-0 bg-background z-10 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Cuenta corriente</p>
            <h2 className="font-display font-black text-2xl tracking-tighter">{client.client_name}</h2>
          </div>
          <button onClick={onClose} className="text-2xl">×</button>
        </div>
        {!data ? <p className="p-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></p> : (
          <div className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <Stat label="Score" value={`${data.health.score}/100`} highlight={RATING_CLS[data.health.rating]?.text} />
              <Stat label="Balance" value={`${currency} ${data.current_balance.toLocaleString()}`} highlight={data.current_balance > 0 ? "text-amber-500" : "text-emerald-500"} />
              <Stat label="% Pagado" value={`${data.health.paid_pct}%`} />
              <Stat label="Demora prom." value={`${data.health.avg_delay_days}d`} />
            </div>
            <table className="w-full text-sm border border-border">
              <thead className="bg-secondary/40 text-xs uppercase tracking-widest">
                <tr><th className="text-left p-3">Fecha</th><th className="text-left p-3">Concepto</th><th className="text-right p-3">Débito</th><th className="text-right p-3">Crédito</th><th className="text-right p-3">Saldo</th></tr>
              </thead>
              <tbody>
                {data.movements.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Sin movimientos en {currency}.</td></tr>}
                {data.movements.map((m, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-2 text-xs">{m.date?.slice(0, 10)}</td>
                    <td className="p-2"><span className="font-mono text-[10px] text-muted-foreground mr-2">{m.ref}</span>{m.title}</td>
                    <td className="p-2 text-right">{m.debit > 0 ? `${currency} ${m.debit.toLocaleString()}` : ""}</td>
                    <td className="p-2 text-right text-emerald-500">{m.credit > 0 ? `${currency} ${m.credit.toLocaleString()}` : ""}</td>
                    <td className={`p-2 text-right font-bold ${m.balance > 0 ? "text-amber-500" : "text-emerald-500"}`}>{currency} {m.balance.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ReminderModal({ invoice, onClose }) {
  const [method, setMethod] = useState("email");
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const send = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/admin/com/invoices/${invoice.id}/remind`, { method, recipient, subject, message });
      setResult(r);
    } catch (e) { setError(formatErr(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-background/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border border-border rounded-sm w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="reminder-modal">
        <h3 className="font-display font-black text-xl mb-1">Recordatorio · {invoice.code}</h3>
        <p className="text-xs text-muted-foreground mb-4">{invoice.client_name} · Saldo {invoice.currency} {(Number(invoice.balance) || 0).toLocaleString()}</p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {[{ k: "email", l: "Email", icon: Send }, { k: "whatsapp", l: "WhatsApp", icon: MessageCircle }].map(({ k, l, icon: Icon }) => (
            <button key={k} onClick={() => setMethod(k)} data-testid={`method-${k}`} className={`flex items-center justify-center gap-2 py-2 rounded-sm border text-sm font-semibold ${method === k ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}><Icon className="w-4 h-4" />{l}</button>
          ))}
        </div>
        <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">{method === "email" ? "Email del cliente" : "WhatsApp con código país (+591…)"}</label>
        <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder={method === "email" ? "cliente@ejemplo.com" : "+591 7XXXXXXX"} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm mb-3" data-testid="reminder-recipient" />
        {method === "email" && (
          <>
            <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Asunto (opcional)</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={`Recordatorio · Factura ${invoice.code}`} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm mb-3" />
          </>
        )}
        <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Mensaje (opcional · usa template si vacío)</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Mensaje personalizado..." className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm mb-3 resize-none" />
        {error && <p className="text-sm text-destructive mb-3">{error}</p>}
        {result && (
          <div className="text-xs bg-emerald-500/10 border border-emerald-500/40 text-emerald-500 p-3 rounded-sm mb-3" data-testid="reminder-result">
            {result.email_sent && <p>✓ Email enviado correctamente.</p>}
            {result.wa_link && (
              <p className="mt-1">Para WhatsApp, hacé click en el botón:
                <a href={result.wa_link} target="_blank" rel="noopener noreferrer" className="block mt-2 px-3 py-2 bg-emerald-500 text-white rounded-sm text-center font-semibold hover:bg-emerald-600">Abrir WhatsApp</a>
              </p>
            )}
            <details className="mt-2 cursor-pointer"><summary>Ver mensaje</summary><pre className="text-[10px] whitespace-pre-wrap mt-1 text-muted-foreground">{result.message}</pre></details>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-sm text-sm">Cerrar</button>
          <button onClick={send} disabled={busy || !recipient} data-testid="reminder-send" className="px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold disabled:opacity-50">
            {busy ? "Enviando..." : (method === "email" ? "Enviar email" : "Generar link WhatsApp")}
          </button>
        </div>
      </div>
    </div>
  );
}
